import {
  Button,
  Card,
  Form,
  Grid,
  Input,
  message,
  Spin,
  Typography,
} from "antd";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ExclamationCircleOutlined,
  LockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import logoIcon from "../../assets/images/eko-favicon.png";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { userRegistrationApi } from "../../services/users.service";

const UserRegistrationComponent = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = !!screens.md && !screens.lg;

  const { Title, Text } = Typography;

  const [form] = Form.useForm();
  const location = useLocation();

  // Extract token and username from the URL
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const username = queryParams.get("username");
  const role = queryParams.get("role");

  const onFinish = (values) => {
    setLoading(true);
    const { username, role, password } = values;

    const newObj = {
      username: username,
      role: role,
      password: password,
      token: token,
    };

    dispatch(
      userRegistrationApi({
        data: newObj,
        finalCallback: () => {
          setLoading(false);
        },
        successCallback: (response) => {
          if (response.success) {
            message.success(response?.msg);

            window.location.href = "/login";
          }
        },
        failureCallback: (error) => { },
      })
    );
  };

  return (
    <div style={styles.container(isMobile)}>
      <Spin spinning={loading} tip="Registering..." size="large">
        <Card style={styles.card(isMobile, isTablet)} bordered={false}>
          <div style={styles.cardContent(isMobile, isTablet)}>
            {/* Header Section */}
            <div style={styles.header(isMobile)}>
              <img src={logoIcon} alt="Company Logo" style={styles.logo(isMobile)} />
              <Title level={2} style={styles.title(isMobile)}>
                Complete Registration
              </Title>
              <Text style={styles.subtitle(isMobile)}>
                Set up your account to get started
              </Text>
            </div>

            {token && username && role ? (
              /* Registration form */
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{ username, token, role }}
                style={styles.form}
              >
                <Form.Item
                  name="username"
                  label={<span style={styles.label(isMobile)}>Username</span>}
                  style={styles.formItem}
                >
                  <Input
                    size="large"
                    disabled
                    prefix={<UserOutlined style={styles.inputIcon} />}
                    style={styles.input(isMobile)}
                  />
                </Form.Item>

                <Form.Item
                  name="role"
                  hidden
                  style={styles.formItem}
                >
                  <Input
                    size="large"
                    disabled
                    prefix={<UserOutlined style={styles.inputIcon} />}
                    style={styles.input(isMobile)}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label={<span style={styles.label(isMobile)}>Password</span>}
                  rules={[
                    { required: true, message: "Please input your password!" },
                  ]}
                  style={styles.formItem}
                >
                  <Input.Password
                    size="large"
                    placeholder="Enter your password"
                    prefix={<LockOutlined style={styles.inputIcon} />}
                    style={styles.input(isMobile)}
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label={<span style={styles.label(isMobile)}>Confirm Password</span>}
                  dependencies={["password"]}
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your password!",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords do not match!")
                        );
                      },
                    }),
                  ]}
                  style={styles.formItem}
                >
                  <Input.Password
                    size="large"
                    placeholder="Confirm your password"
                    prefix={<LockOutlined style={styles.inputIcon} />}
                    style={styles.input(isMobile)}
                  />
                </Form.Item>

                <Form.Item style={styles.submitFormItem}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    size="large"
                    style={styles.button(isMobile)}
                  >
                    {loading ? "Registering..." : "Complete Registration"}
                  </Button>
                </Form.Item>
              </Form>
            ) : (
              <div style={styles.errorContainer(isMobile)}>
                <ExclamationCircleOutlined style={styles.errorIcon(isMobile)} />
                <Title level={3} style={styles.errorTitle(isMobile)}>
                  Invalid Invitation Link
                </Title>
                <Text style={styles.errorText(isMobile)}>
                  This invitation link is invalid or has expired. Please contact your administrator for a new invitation.
                </Text>
              </div>
            )}
          </div>
        </Card>
      </Spin>
    </div>
  );
};

// Styling for the page - matching login page design
const styles = {
  container: (isMobile) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100dvh",
    padding: isMobile ? "16px 12px" : "20px",
    width: "100%",
    overflowY: "auto",
  }),
  card: (isMobile, isTablet) => ({
    width: "100%",
    maxWidth: isMobile ? "100%" : "550px",
    minWidth: isMobile ? "100%" : "550px",
    padding: 0,
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
    borderRadius: "16px",
    backgroundColor: "#fff",
    overflow: "hidden",
  }),
  cardContent: (isMobile, isTablet) => ({
    padding: isMobile ? "24px 16px" : isTablet ? "36px 28px" : "48px 40px",
  }),
  header: (isMobile) => ({
    textAlign: "center",
    marginBottom: isMobile ? "24px" : "40px",
  }),
  logo: (isMobile) => ({
    display: "block",
    margin: "0 auto 16px auto",
    height: isMobile ? 56 : 80,
    width: "auto",
    maxWidth: "100%",
  }),
  title: (isMobile) => ({
    margin: "0 0 8px 0",
    color: "#1a1a1a",
    fontWeight: "600",
    fontSize: isMobile ? "24px" : "28px",
    lineHeight: isMobile ? "30px" : "36px",
  }),
  subtitle: (isMobile) => ({
    color: "#666",
    fontSize: isMobile ? "14px" : "16px",
    margin: 0,
  }),
  form: {
    width: "100%",
  },
  formItem: {
    marginBottom: "24px",
  },
  submitFormItem: {
    marginBottom: 0,
    marginTop: "32px",
  },
  label: (isMobile) => ({
    fontWeight: "600",
    color: "#333",
    fontSize: isMobile ? "13px" : "14px",
  }),
  input: (isMobile) => ({
    borderRadius: "8px",
    border: "1px solid #d9d9d9",
    fontSize: isMobile ? "15px" : "16px",
    transition: "all 0.3s ease",
    height: isMobile ? "44px" : "48px",
    width: "100%",
  }),
  inputIcon: {
    color: "#999",
  },
  button: (isMobile) => ({
    width: "100%",
    backgroundColor: "#1890ff",
    borderColor: "#1890ff",
    fontWeight: "600",
    fontSize: isMobile ? "15px" : "16px",
    height: isMobile ? "44px" : "48px",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(24, 144, 255, 0.3)",
    transition: "all 0.3s ease",
  }),
  errorContainer: (isMobile) => ({
    textAlign: "center",
    padding: isMobile ? "24px 12px" : "40px 20px",
  }),
  errorIcon: (isMobile) => ({
    color: "#ff4d4f",
    fontSize: isMobile ? "40px" : "48px",
    marginBottom: "16px",
  }),
  errorTitle: (isMobile) => ({
    margin: "0 0 16px 0",
    color: "#1a1a1a",
    fontWeight: "600",
    fontSize: isMobile ? "20px" : "24px",
  }),
  errorText: (isMobile) => ({
    color: "#666",
    fontSize: isMobile ? "14px" : "16px",
    lineHeight: "1.5",
  }),
};

export default UserRegistrationComponent;
